import BugsnagPluginReact from '@bugsnag/plugin-react';
import { clientMount } from '@vp/ubik-fragment-react';
import React from 'react';
import Bugsnag from '@bugsnag/js';
import { initialize, waitTillAvailable } from '@vp/ab-reader';
import i18n from 'i18next';
import type { MountWithRootOptions } from '@vp/ubik-fragment-types';
import { App } from '../app';
import type { Props } from '../app';

type AppEnvironment = 'DEV' | 'STAGING' | 'PROD';

function getEnv(): AppEnvironment {
    if (window.location.hostname.includes('localhost')) {
        return 'DEV';
    }
    if (window.location.hostname.includes('staging')) {
        return 'STAGING';
    }
    return 'PROD';
}

export const mount: MountWithRootOptions<Props> = async (rootElement, renderProps, rootOptions) => {
    const { localeReference, locale } = renderProps;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line no-restricted-properties
    const stringsTable = await System.import(localeReference);

    // Initialize i18n once per browser session, then per-mount: register the
    // locale bundle and activate it. Keeps locale switches (e.g. via UBIK
    // tooling re-mount, or future client-side language changes) correct without
    // requiring the React tree to reason about i18n state.
    if (!i18n.isInitialized) {
        await i18n.init({
            lowerCaseLng: true, // locales are set to lower case e.g. "en-us" not "en-US"
            interpolation: {
                escapeValue: false, // React already provides escaping
            },
            nsSeparator: '*****', // namespace selector is a string delimited which defaults to ":". Overriding to *** so that urls don't get split
            returnObjects: true,
        });
    }
    i18n.addResourceBundle(locale, 'translation', stringsTable, true, true);
    await i18n.changeLanguage(locale);

    // This causes the fragment to wait until we have an active ab-reader before rendering
    initialize();
    await waitTillAvailable(1000);

    Bugsnag.start({
        apiKey: process.env.DESIGN_SERVICES_DASHBOARD_BUGSNAG_API_KEY ?? '',
        enabledReleaseStages: ['production'],
        releaseStage: process.env.DESIGN_SERVICES_DASHBOARD_BUGSNAG_RELEASE_STAGE,
        autoTrackSessions:
            process.env.DESIGN_SERVICES_DASHBOARD_BUGSNAG_RELEASE_STAGE === 'production',
        plugins: [new BugsnagPluginReact(React)],
        onError: (event) => {
            // ignore errors originating from node_modules
            const stacktrace = event.errors[0]?.stacktrace;
            if (stacktrace && stacktrace[0]?.file?.includes('node_modules')) {
                return false;
            }

            // group noisy js errors
            const errorStrings = [
                'unhandledrejection handler received a non-error. See "unhandledrejection handler" tab for more detail.',
                'Unhandled Promise Rejection:',
            ];
            if (errorStrings.some((s) => event.errors[0]?.errorMessage.includes(s))) {
                event.groupingHash = 'Unhandled Promise Rejection error';
            }

            event.addMetadata('request', {
                url: event.request.url,
                headers: event.request.headers,
            });

            event.addMetadata('debug info', {
                isUbik: 'true',
                errorLocation: typeof window === 'undefined' ? 'server' : 'client',
            });
        },
    });

    const bugsnagPlugin = Bugsnag.getPlugin('react');
    const ErrorBoundary = bugsnagPlugin?.createErrorBoundary() ?? React.Fragment;

    const environment = getEnv();

    return clientMount(
        <ErrorBoundary>
            <App {...renderProps} environment={environment} />
        </ErrorBoundary>,
        rootElement,
        rootOptions
    );
};
